import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaLink,
  FaLinkedinIn,
  FaTwitter,
  FaWhatsapp,
} from 'react-icons/fa';
import { getPostBySlug, getCategoryLabel } from '../../data/posts';
import '../Blog/Blogpost.css';

/* ── Header atom ──
   The layout-critical styles below are inline on purpose, so the
   decorative drawing can never push the title around or overflow
   the page, even if the stylesheet is out of date. */
const HEADER_STYLE = { position: 'relative', overflow: 'hidden' };
const HEADER_INNER_STYLE = { position: 'relative', zIndex: 1 };
const ATOM_STYLE = {
  position: 'absolute',
  top: '50%',
  right: '-60px',
  width: 'min(520px, 75vw)',
  height: 'min(520px, 75vw)',
  transform: 'translateY(-50%)',
  color: '#C9A84C',
  opacity: 0.28,
  pointerEvents: 'none',
};

const ORBIT_PATH = 'M370,200 A170,62 0 1,1 30,200 A170,62 0 1,1 370,200 Z';
const ORBITS = [
  { angle: 0,   dur: '11s', begin: '0s'  },
  { angle: 60,  dur: '14s', begin: '-4s' },
  { angle: 120, dur: '17s', begin: '-9s' },
];

function Atom() {
  // Electrons stay still for people who prefer reduced motion
  const still =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <svg
      className="article-header__atom"
      style={ATOM_STYLE}
      viewBox="0 0 400 400"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        {ORBITS.map(({ angle }) => (
          <ellipse
            key={angle}
            cx="200"
            cy="200"
            rx="170"
            ry="62"
            transform={`rotate(${angle} 200 200)`}
          />
        ))}
      </g>

      <circle cx="200" cy="200" r="34" fill="currentColor" opacity="0.25" />
      <circle cx="200" cy="200" r="14" fill="currentColor" />

      {ORBITS.map(({ angle, dur, begin }) => (
        <g
          key={angle}
          fill="currentColor"
          transform={`rotate(${angle} 200 200)`}
        >
          <circle r="7" cx={still ? 370 : 0} cy={still ? 200 : 0}>
            {!still && (
              <animateMotion
                dur={dur}
                begin={begin}
                repeatCount="indefinite"
                path={ORBIT_PATH}
              />
            )}
          </circle>
        </g>
      ))}
    </svg>
  );
}

/* A `content` item is either a plain string (a paragraph) or an object:
   { type: 'heading', text }  |  { type: 'list', items: [...] } */
function ContentBlock({ block, isFirst }) {
  if (typeof block === 'string') {
    return <p className={isFirst ? 'article-lead' : undefined}>{block}</p>;
  }

  if (block.type === 'heading') {
    return <h2 className="article-heading">{block.text}</h2>;
  }

  if (block.type === 'list') {
    const ListTag = block.ordered ? 'ol' : 'ul';
    return (
      <ListTag
        className={`article-list${block.ordered ? ' article-list--ordered' : ''}`}
      >
        {block.items.map((item, i) => (
          <li key={i}>
            {typeof item === 'string' ? (
              <strong>{item}</strong>
            ) : (
              <>
                <strong>{item.lead}</strong> {item.text}
              </>
            )}
          </li>
        ))}
      </ListTag>
    );
  }

  return <p>{block.text}</p>;
}

function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  const bodyRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  // Start at the top and set the browser tab title
  useEffect(() => {
    window.scrollTo(0, 0);

    if (!post) return undefined;
    const previousTitle = document.title;
    document.title = `${post.title} | Nuclear Society of Kenya`;
    return () => {
      document.title = previousTitle;
    };
  }, [post]);

  // Reading progress: how far through the article body the reader is
  useEffect(() => {
    if (!post) return undefined;

    const update = () => {
      const el = bodyRef.current;
      if (!el) return;
      const { top, height } = el.getBoundingClientRect();
      const read = window.innerHeight * 0.5 - top;
      setProgress(Math.min(1, Math.max(0, read / height)));
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [post]);

  /* ── Article not found ── */
  if (!post) {
    return (
      <section className="article-missing">
        <h1 className="article-missing__title">Article not found</h1>
        <p className="article-missing__text">
          This article doesn't exist or hasn't been published yet.
        </p>
        <Link to="/blog" className="btn btn--primary">
          Back to all articles
        </Link>
      </section>
    );
  }

  const {
    category, date, title, excerpt, author, readTime, content, pullQuote,
  } = post;

  const pageUrl = window.location.href;
  const shareLinks = [
    {
      label: 'Share on WhatsApp',
      href: `https://wa.me/?text=${encodeURIComponent(`${title} ${pageUrl}`)}`,
      icon: <FaWhatsapp size={16} />,
    },
    {
      label: 'Share on LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`,
      icon: <FaLinkedinIn size={16} />,
    },
    {
      label: 'Share on X',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(pageUrl)}`,
      icon: <FaTwitter size={16} />,
    },
  ];

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      window.prompt('Copy this link:', pageUrl);
    }
  }

  return (
    <article className="article">
      {/* Reading progress bar */}
      <div className="article-progress" aria-hidden="true">
        <div
          className="article-progress__bar"
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>

      {/* ── Header ── */}
      <header className="article-header" style={HEADER_STYLE}>
        <Atom />

        <div className="article-header__inner" style={HEADER_INNER_STYLE}>
          <Link to="/blog" className="article-back">
            <FaArrowLeft size={12} aria-hidden="true" />
            All articles
          </Link>

          <div className="article-meta">
            <span className="article-tag">{getCategoryLabel(category)}</span>
            <time>{date}</time>
            <span>{readTime}</span>
          </div>

          <h1 className="article-title">{title}</h1>

          {excerpt && <p className="article-standfirst">{excerpt}</p>}

          <div className="article-byline">
            <div className="article-byline__avatar" aria-hidden="true">
              {author.initials}
            </div>
            <div>
              <div className="article-byline__name">By {author.name}</div>
              <div className="article-byline__role">{author.role}</div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="article-body" ref={bodyRef}>
        {content.map((block, index) => (
          <React.Fragment key={index}>
            <ContentBlock block={block} isFirst={index === 0} />

            {pullQuote && pullQuote.after === index + 1 && (
              <blockquote className="article-quote">
                <p>“{pullQuote.text}”</p>
                <footer>{author.name}</footer>
              </blockquote>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── End of article ── */}
      <footer className="article-end">
        {author.bio && (
          <div className="article-author">
            <div className="article-author__avatar" aria-hidden="true">
              {author.initials}
            </div>
            <div>
              <div className="article-author__label">About the author</div>
              <div className="article-author__name">{author.name}</div>
              <p className="article-author__bio">{author.bio}</p>
            </div>
          </div>
        )}

        <div className="article-share">
          <span className="article-share__label">Share this article</span>
          <div className="article-share__buttons">
            {shareLinks.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                className="article-share__btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
              >
                {icon}
              </a>
            ))}
            <button
              type="button"
              className="article-share__copy"
              onClick={copyLink}
            >
              <FaLink size={13} aria-hidden="true" />
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
        </div>

        <Link to="/blog" className="article-end__back">
          <FaArrowLeft size={12} aria-hidden="true" />
          Back to all articles
        </Link>
      </footer>
    </article>
  );
}

export default BlogPost;